import { useTranslation } from 'next-i18next'
import { PieChart } from 'react-minimal-pie-chart'

import { Link } from '~/components/Link'
import Spinner from '~/components/Spinner'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import MasteryButton from '~/components/Buttons/MasteryButton'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import ChangeButtonIcon from '~/components/Icons/svgs/ChangeButtonIcon'
import { useGetInCurriculumV2 } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { HighlightIds } from '~/checklist/constants'

import useMasteryTrackPanelModel from '../hooks/useMasteryTrackPanelModel'

const MasteryTrackPanel = ({
  masteryTrackPanelRef,
}: {
  masteryTrackPanelRef: React.RefObject<HTMLDivElement | null>
}): React.ReactElement => {
  const { t } = useTranslation(['dashboard', 'onboarding', 'curriculum'])
  const isCurriculumV2Enabled = useGetInCurriculumV2()
  const {
    gradeData: data,
    isLoading,
    isGradeLoading,
    progressPercentageData,
    title,
    browseCurriculumUrl,
    startLinkUrl,
    onChangeCurriculumGrade,
    onAddCurriculum,
    mustKnowsProgressCardProps,
    topicProgressPercentage,
    hasSubtopicStats,
  } = useMasteryTrackPanelModel()

  const progressPercentage = isCurriculumV2Enabled
    ? topicProgressPercentage
    : progressPercentageData?.progressPercentage
  const showProgressCard = isCurriculumV2Enabled
    ? Boolean(data?.subjectOrder && data?.subTopic && (hasSubtopicStats || topicProgressPercentage === undefined))
    : Boolean(data?.subjectOrder)

  return (
    <div
      className="bg-purple-cosmos border border-white-veil rounded-4 min-h-80 md:min-h-72"
      id={HighlightIds.MASTERY_TRACK}
      ref={masteryTrackPanelRef}
    >
      {isLoading || isGradeLoading ? (
        <div className="flex justify-center items-center rounded-4 w-full h-full">
          <Spinner size="large" />
        </div>
      ) : data?.grade ? (
        <div className="flex flex-col gap-5 lg:gap-4 p-4 h-full">
          <div className="flex justify-between items-center gap-2">
            <h2 className="font-bold text-white text-sm lg:text-4.5 leading-6">{t('dashboard:yourMasteryTrack')}</h2>
            <Link
              className="flex items-center gap-1 lg:bg-purple-darker/50 lg:py-1 lg:pr-3 lg:pl-1 lg:border lg:border-purple-half-deep lg:rounded-large text-blue-icy lg:text-white hover:scale-110 duration-300 w-fit"
              to={browseCurriculumUrl}
            >
              <ResponsiveImage
                alt="X"
                className="hidden lg:block"
                height={1313}
                imageClassName="w-8"
                imageWidths="24, 36, 48"
                srcKey="curriculum/curriculum-bulb.png"
                width={1920}
              />
              <span className="lg:hidden font-semibold text-3.25 leading-5">{t('dashboard:browseCurriculum')}</span>
              <span className="hidden lg:inline font-semibold text-xs leading-5">
                {t('dashboard:browseGrade', { grade: title })}
              </span>
            </Link>
          </div>
          <div className="flex flex-col gap-1">
            <button className="flex items-center font-bold text-2xl w-fit" onClick={onChangeCurriculumGrade}>
              <span>{title}</span>
              <SVGIcon className="mx-2.5 w-3.5 h-4.5" icon={ChangeButtonIcon} size={24} />
            </button>
            <p className="font-normal text-blue-pastel text-xs leading-5">
              {data?.subjectOrder ? t('onboarding:continueWhereYouLeftOff') : t('onboarding:getStart')}
            </p>
          </div>
          {showProgressCard ? (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3 p-4 border border-blue-pastel rounded-4">
                <div className="relative w-20 h-20 shrink-0">
                  <PieChart
                    className="top-0 left-0 absolute"
                    data={[
                      { value: 100, color: 'white' },
                      { value: progressPercentage ?? 0, color: '#7D38D6' },
                    ]}
                    lineWidth={18}
                    rounded
                    startAngle={-90}
                    totalValue={100}
                  />
                  <div className="absolute inset-0 flex justify-center items-center w-full">
                    {progressPercentage === undefined ? (
                      <Spinner size="small" />
                    ) : (
                      <span className="font-bold text-5 text-white">{Math.floor(progressPercentage)}%</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-0.5">
                  <h3 className="font-semibold text-base leading-6">{data?.subTopic}</h3>
                  {isCurriculumV2Enabled ? (
                    <>
                      <h4 className="pb-0.5 font-normal text-blue-pastel text-3.25 lg:text-xs leading-5">
                        {data?.subject}
                      </h4>
                      {mustKnowsProgressCardProps.total > 0 && (
                        <div className="inline-flex items-center gap-2 bg-purple-500/30 px-3 py-2 rounded-full w-fit">
                          <span className="mr-1 w-3 h-5 text-green-400">🧩</span>
                          <p className="font-semibold text-white text-xs leading-5">
                            <span>{t('curriculum:mustKnow')}:</span>{' '}
                            <span>
                              {mustKnowsProgressCardProps.completed}/{mustKnowsProgressCardProps.total}
                            </span>
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    data?.subject &&
                    data?.subjectOrder && <h4 className="pb-0.5 font-medium text-5 leading-8">{data?.subject}</h4>
                  )}
                </div>
              </div>
              <MasteryButton label={t('dashboard:continueLearning')} to={startLinkUrl} />
            </div>
          ) : isCurriculumV2Enabled && data?.subjectOrder ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-1 justify-center items-center p-4 border border-blue-pastel rounded-4">
                <h3 className="font-medium text-base leading-6">{data?.subject}</h3>
              </div>
              <MasteryButton label={t('dashboard:start')} to={startLinkUrl} />
            </div>
          ) : (
            <div className="flex flex-col flex-1 justify-center items-center gap-3 border border-blue-pastel rounded-4 h-full">
              <p>{t('dashboard:chooseTopicToBegin')}</p>
              <MasteryButton
                label={t('dashboard:chooseTopic')}
                to={typeof startLinkUrl === 'string' ? startLinkUrl : undefined}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col justify-center items-center gap-4 h-full">
          <div className="font-medium text-4.5 leading-6">{t('dashboard:startYourLearningJourney')}</div>
          <div className="font-light text-blue-pastel text-xs leading-5">{t('dashboard:selectGradeToBegin')}</div>
          <MasteryButton label={t('onboarding:curriculum.selectGrade')} onClick={onAddCurriculum} />
        </div>
      )}
    </div>
  )
}

export default MasteryTrackPanel
